<template>
  <div v-if="!isMatchInfoConfirmed" class="match-info">
    
    <div class="match-setup">
      <h2>Match Setup</h2>
      <p>Select up to 12 regular players and up to 2 liberos. Minimum total is 6.</p>
      
      <div class="match-details">
        <label>
          Our Team Name
          <input type="text" v-model="matchInfo.teamName" placeholder="Moss Side Jaguars" />
        </label>
        <label>
          Date
          <input type="date" v-model="matchInfo.date" />
        </label>
        <label>
          Home/Away
          <select v-model="matchInfo.homeAway">
            <option value="">Select</option>
            <option value="Home">Home</option>
            <option value="Away">Away</option>
          </select>
        </label>
        <label>
          Opposition
          <input type="text" v-model="matchInfo.opposition" />
        </label>
        <label>
          Competition
          <input type="text" v-model="matchInfo.competition" />
        </label>
      </div>
      
      <div class="match-actions">
        <button type="button" @click="fillUpcomingMatch" class="btn primary">Auto-fill upcoming match</button>
        <button type="button" class="btn primary" @click="confirmMatchInfo">Confirm Match Info</button>
        <button type="button" class="btn secondary" @click="$emit('open-archives')">Saved Matches</button>
      </div>
      
    </div>
    
  </div>
  
  <div v-else class="match-roster">
    
    <div class="roster-actions">
      <button type="button" class="btn secondary" @click="goBackToMatchInfo">Back to Match Info</button>
    </div>
    
    <div class="counts">
      <span>Regular: {{ regularCount }}/12</span>
      <span>Liberos: {{ liberoCount }}/2</span>
      <span>Total: {{ totalCount }}/14</span>
    </div>
    
    <div class="bulk-actions">
      <button type="button" @click="selectAllEligible" class="btn primary">Select All</button>
      <button type="button" @click="deselectAll" class="btn secondary">Deselect All</button>
    </div>
    
    <table class="roster-table">
      <thead>
        <tr>
          <th></th>
          <th>#</th>
          <th>Name</th>
          <th>Position</th>
          <th>Regular</th>
          <th>Libero</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="player in teamPlayers" :key="player.id" :class="{ inactive: !isActive(player) }">
          <td>
            <img
              v-if="getPhotoUrl(player)"
              class="roster-photo"
              :src="getPhotoUrl(player)"
              :alt="player.name"
            />
          </td>
          <td>
            <input
              type="number"
              class="number-input"
              :class="{ conflict: isNumberConflict(player) }"
              :value="player.number"
              min="0"
              @input="updatePlayerNumber(player.id, $event.target.value)"
            />
          </td>
          <td>{{ player.name }}</td>
          <td>{{ player.position }}</td>
          <td>
            <input
              v-if="isRegularEligible(player)"
              type="checkbox"
              :checked="isRegularSelected(player.id)"
              :disabled="isRegularDisabled(player.id) || !isActive(player)"
              @change="toggleRegular(player.id, $event.target.checked)"
            />
          </td>
          <td>
            <input
              v-if="isLiberoEligible(player)"
              type="checkbox"
              :checked="isLiberoSelected(player.id)"
              :disabled="isLiberoDisabled(player.id) || !isActive(player)"
              @change="toggleLibero(player.id, $event.target.checked)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    
    <div v-if="numberError" class="error-message">{{ numberError }}</div>
    <button @click="confirmRoster" :disabled="!canConfirm" class="btn primary">Confirm Match Roster</button>
  </div>
</template>

<script>
import teamPlayers from '../team.js';
import matchCalendar from '../matchCalendar.js';

export default {
  name: 'MatchSetup',
  props: {
    initialRoster: {
      type: Array,
      default: () => []
    },
    initialMatchInfo: {
      type: Object,
      default: null
    }
  },
  watch: {
    initialMatchInfo: {
      immediate: true,
      handler(next) {
        // Ensure teamName defaults to 'Moss Side Jaguars' if empty
        this.matchInfo = next ? {
          teamName: next.teamName || 'Moss Side Jaguars',
          date: next.date || '',
          homeAway: next.homeAway || '',
          opposition: next.opposition || '',
          competition: next.competition || ''
        } : {
          teamName: 'Moss Side Jaguars',
          date: '',
          homeAway: '',
          opposition: '',
          competition: ''
        };
      }
    }
  },
  data() {
    const rosterMap = new Map(
    (Array.isArray(this.initialRoster) ? this.initialRoster : [])
    .filter(player => player && player.id !== undefined)
    .map(player => [player.id, player])
    );
    return {
      teamPlayers: teamPlayers.map(player => {
        const existing = rosterMap.get(player.id);
        return {
          ...player,
          number: existing && existing.number !== undefined ? existing.number : player.shirtNumber,
          name: `${player.firstName} ${player.lastName}`
        };
      }),
      matchInfo: this.initialMatchInfo ? {
        teamName: this.initialMatchInfo.teamName || 'Moss Side Jaguars',
        date: this.initialMatchInfo.date || '',
        homeAway: this.initialMatchInfo.homeAway || '',
        opposition: this.initialMatchInfo.opposition || '',
        competition: this.initialMatchInfo.competition || ''
      } : {
        teamName: 'Moss Side Jaguars',
        date: '',
        homeAway: '',
        opposition: '',
        competition: ''
      },
      selectionById: (Array.isArray(this.initialRoster)
      ? this.initialRoster.reduce((acc, player) => {
        if (player && player.id !== undefined && player.playerType) {
          acc[player.id] = player.playerType;
        }
        return acc;
      }, {})
      : {}),
      isMatchInfoConfirmed: Array.isArray(this.initialRoster) && this.initialRoster.length > 0
    };
  },
  computed: {
    regularCount() {
      return Object.values(this.selectionById).filter(value => value === 'regular').length;
    },
    liberoCount() {
      return Object.values(this.selectionById).filter(value => value === 'libero').length;
    },
    totalCount() {
      return this.regularCount + this.liberoCount;
    },
    canConfirm() {
      return this.totalCount >= 6 &&
      this.totalCount <= 14 &&
      this.regularCount <= 12 &&
      this.liberoCount <= 2 &&
      !this.numberError;
    },
    numberError() {
      const selectedIds = Object.keys(this.selectionById);
      if (!selectedIds.length) {
        return '';
      }
      const selectedPlayers = this.teamPlayers.filter(player => selectedIds.includes(String(player.id)));
      const numbers = selectedPlayers.map(player => player.number);
      const missing = numbers.some(number => number === null || number === undefined || number === '');
      if (missing) {
        return 'All selected player\'s shirt numbers are required.';
      }
      const duplicates = numbers.filter((value, index, array) => array.indexOf(value) !== index);
      if (duplicates.length) {
        return 'Selected player shirt numbers must be unique.';
      }
      return '';
    },
    duplicateNumbers() {
      const selectedIds = Object.keys(this.selectionById);
      if (!selectedIds.length) {
        return new Set();
      }
      const selectedPlayers = this.teamPlayers.filter(player => selectedIds.includes(String(player.id)));
      const counts = new Map();
      selectedPlayers.forEach(player => {
        const number = player.number;
        if (number === null || number === undefined || number === '') {
          return;
        }
        counts.set(number, (counts.get(number) || 0) + 1);
      });
      return new Set(
      Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([number]) => number)
      );
    }
  },
  methods: {
    confirmMatchInfo() {
      this.isMatchInfoConfirmed = true;
    },
    goBackToMatchInfo() {
      this.isMatchInfoConfirmed = false;
    },
    getPhotoUrl(player) {
      try {
        if (player && player.photo) {
          return require(`@/assets/${player.photo}`);
        }
        return require('@/assets/player-photos/placeholder.png');
      } catch (error) {
        return require('@/assets/player-photos/placeholder.png');
      }
    },
    isActive(player) {
      return !player.status || player.status === 'active';
    },
    updatePlayerNumber(playerId, value) {
      const nextValue = value === '' ? '' : Number(value);
      this.teamPlayers = this.teamPlayers.map(player => (
      player.id === playerId ? { ...player, number: nextValue } : player
      ));
    },
    isNumberConflict(player) {
      if (!player || player.number === null || player.number === undefined || player.number === '') {
        return false;
      }
      if (!this.selectionById[player.id]) {
        return false;
      }
      return this.duplicateNumbers.has(player.number);
    },
    fillUpcomingMatch() {
      const today = new Date();
      const todayKey = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
      .toISOString()
      .slice(0, 10);
      const upcomingMatch = matchCalendar.find(match => match.date >= todayKey);
      
      if (!upcomingMatch) {
        return;
      }
      
      this.matchInfo = {
        date: upcomingMatch.date,
        homeAway: upcomingMatch.venueType,
        opposition: upcomingMatch.opponent,
        competition: upcomingMatch.competition
      };
    },
    isRegularSelected(playerId) {
      return this.selectionById[playerId] === 'regular';
    },
    isLiberoSelected(playerId) {
      return this.selectionById[playerId] === 'libero';
    },
    isLiberoEligible(player) {
      return player.position.toLowerCase().includes('libero');
    },
    isRegularEligible(player) {
      return !this.isLiberoEligible(player);
    },
    selectAllEligible() {
      const nextSelections = {};
      const regularPlayers = this.teamPlayers.filter(player => this.isRegularEligible(player) && this.isActive(player));
      const liberoPlayers = this.teamPlayers.filter(player => this.isLiberoEligible(player) && this.isActive(player));
      
      regularPlayers.slice(0, 12).forEach(player => {
        nextSelections[player.id] = 'regular';
      });
      liberoPlayers.slice(0, 2).forEach(player => {
        nextSelections[player.id] = 'libero';
      });
      
      this.selectionById = nextSelections;
    },
    deselectAll() {
      this.selectionById = {};
    },
    toggleRegular(playerId, isChecked) {
      const selectedPlayer = this.teamPlayers.find(player => player.id === playerId);
      if (!selectedPlayer || !this.isActive(selectedPlayer)) {
        return;
      }
      const nextSelections = { ...this.selectionById };
      if (!isChecked) {
        delete nextSelections[playerId];
        this.selectionById = nextSelections;
        return;
      }
      nextSelections[playerId] = 'regular';
      this.selectionById = nextSelections;
    },
    toggleLibero(playerId, isChecked) {
      const selectedPlayer = this.teamPlayers.find(player => player.id === playerId);
      if (!selectedPlayer || !this.isActive(selectedPlayer)) {
        return;
      }
      const nextSelections = { ...this.selectionById };
      if (!isChecked) {
        delete nextSelections[playerId];
        this.selectionById = nextSelections;
        return;
      }
      nextSelections[playerId] = 'libero';
      this.selectionById = nextSelections;
    },
    isRegularDisabled(playerId) {
      const current = this.selectionById[playerId];
      return current !== 'regular' && this.regularCount >= 12;
    },
    isLiberoDisabled(playerId) {
      const current = this.selectionById[playerId];
      return current !== 'libero' && this.liberoCount >= 2;
    },
    confirmRoster() {
      const buildRosterPlayer = (player, playerType) => ({
        id: player.id,
        number: player.number,
        name: player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
        playerType,
        photo: player.photo || ''
      });
      const roster = this.teamPlayers
      .filter(player => this.selectionById[player.id])
      .map(player => buildRosterPlayer(player, this.selectionById[player.id]));
      this.$emit('match-setup', { roster, metadata: this.matchInfo });
    }
  }
};
</script>

<style scoped>
.match-setup {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
}

.match-roster {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
}

.roster-photo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  -o-object-fit: cover;
  object-fit: cover;
  display: block;
  background: var(--grey-20);
}

.counts {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: 12px 0;
  font-weight: 600;
}

.match-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 1fr));
  gap: 10px 12px;
  margin: 10px auto 16px;
  text-align: left;
}

.match-details label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.match-actions {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  margin: 0 0 12px;
}

.roster-actions {
  display: flex;
  justify-content: flex-start;
  margin: 0 0 8px;
}

.bulk-actions {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 8px 0 12px;
}

.roster-table {
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  background-color: var(--white);
  color: var(--grey-900);
}

.roster-table th,
.roster-table td {
  border: 1px solid var(--grey-50);
  padding: 4px 8px;
  font-size: 12px;
}

.roster-table tr.inactive {
  color: var(--grey-300);
  background-color: var(--grey-5);
  
  .roster-table tr.inactive {
    color: var(--grey-300);
    background-color: var(--grey-5);
  
  .roster-table tr.inactive {
    color: var(--grey-300);
    background-color: var(--grey-5);
  }
  }
}

.roster-table tr.inactive input,
.roster-table tr.inactive select {
  color: var(--grey-300);
  background-color: var(--grey-5);
}

.number-input {
  width: 64px;
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid;
  border-radius: 4px;
}

.number-input.conflict {
  border-color: var(--red-300);
  background-color: var(--red-25);
}

.error-message {
  margin: 8px 0;
  color: var(--red-300);
  font-weight: 700;
}
</style>
